<script setup lang="ts">
import { Link, router } from "@inertiajs/vue3"
import { reactive } from "vue"
import AppShell from "../Layout/AppShell.vue"
import { UiButton, UiDataTable } from "@/components"
import { useConfirmation } from "@/composables/useConfirmation"
import { useNotifications } from "@/composables/useNotifications"
import type { UiDataTableColumn, UiDataTableFilter } from "@/types/ui"

const { confirm } = useConfirmation()
const notify = useNotifications()

type SupplierRow = {
  id: number
  company: string
  name: string
  document: string
  email: string
  can_edit: boolean
  can_destroy: boolean
}

const props = defineProps<{
  suppliers: SupplierRow[]
  companies: Array<{ id: number; name: string }>
  query: {
    q?: string
    company_id?: string
    page: number
    per_page: number
  }
  pagination: {
    page: number
    perPage: number
    total: number
  }
  can_create: boolean
}>()

const columns: UiDataTableColumn[] = [
  { key: "company", label: "Empresa" },
  { key: "name", label: "Fornecedor" },
  { key: "document", label: "CNPJ" },
  { key: "email", label: "E-mail" },
  { key: "actions", label: "Acoes", align: "right" },
]

const filters: UiDataTableFilter[] = [
  {
    key: "company_id",
    label: "Empresa",
    options: props.companies.map((company) => ({ label: company.name, value: company.id })),
  },
]

const selectedFilters = reactive<Record<string, string | number | "">>({
  company_id: props.query.company_id ?? "",
})

function fetchWithQuery(overrides: Record<string, string | number | ""> = {}) {
  const params = {
    q: String(overrides.q ?? props.query.q ?? ""),
    company_id: String(overrides.company_id ?? selectedFilters.company_id ?? ""),
    page: Number(overrides.page ?? props.pagination.page),
    per_page: Number(overrides.per_page ?? props.pagination.perPage),
  }

  router.get("/suppliers", params, {
    preserveState: true,
    preserveScroll: true,
    replace: true,
  })
}

function updateSearch(value: string) {
  fetchWithQuery({ q: value, page: 1 })
}

function updateFilter({ key, value }: { key: string; value: string | number | "" }) {
  selectedFilters[key] = value
  fetchWithQuery({ [key]: value, page: 1 })
}

function changePage(page: number) {
  fetchWithQuery({ page })
}

function changePerPage(perPage: number) {
  fetchWithQuery({ per_page: perPage, page: 1 })
}

async function destroySupplier(id: number) {
  const ok = await confirm({
    title: "Excluir fornecedor",
    message: "Deseja excluir este fornecedor?",
    type: "danger",
    confirmText: "Excluir",
  })
  if (!ok) return

  router.delete(`/suppliers/${id}`, {
    preserveScroll: true,
    onSuccess: () => notify.success("Fornecedor excluido com sucesso"),
  })
}
</script>

<template>
  <AppShell>
    <div class="space-y-6">
      <section class="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h1 class="text-2xl font-semibold text-slate-900">Fornecedores</h1>
        <p class="mt-2 text-sm text-slate-600">Gerencie fornecedores por empresa e use-os para custos de materia-prima.</p>
      </section>

      <UiDataTable
        :columns="columns"
        :rows="suppliers"
        :search="query.q ?? ''"
        :filters="filters"
        :selected-filters="selectedFilters"
        :pagination="pagination"
        @update:search="updateSearch"
        @update:filter="updateFilter"
        @page-change="changePage"
        @per-page-change="changePerPage"
      >
        <template #toolbar-actions>
          <Link
            v-if="can_create"
            href="/suppliers/new"
            class="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
          >
            Novo fornecedor
          </Link>
        </template>

        <template #cell="{ row, column, value }">
          <div v-if="column.key === 'actions'" class="flex justify-end gap-2">
            <Link
              v-if="(row as SupplierRow).can_edit"
              :href="`/suppliers/${(row as SupplierRow).id}/edit`"
              class="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-100"
            >
              Editar
            </Link>

            <UiButton v-if="(row as SupplierRow).can_destroy" size="sm" variant="danger" @click="destroySupplier((row as SupplierRow).id)">
              Excluir
            </UiButton>
          </div>

          <span v-else>{{ value || "-" }}</span>
        </template>
      </UiDataTable>
    </div>
  </AppShell>
</template>
